import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getCurrencies, getInsurance, getUsers } from '../services/api';

const StatCard = ({ label, value, icon, color, onClick }) => (
  <div
    onClick={onClick}
    style={{
      background:'#fff',borderRadius:12,
      border:'1px solid #E2E8F0',
      padding:'16px 18px',
      cursor: onClick ? 'pointer' : 'default',
      transition:'transform .15s,box-shadow .15s,border-color .15s',
      display:'flex',alignItems:'center',gap:14,
    }}
    onMouseEnter={e => { if(onClick) { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,.09)'; e.currentTarget.style.borderColor=color; }}}
    onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=''; e.currentTarget.style.borderColor='#E2E8F0'; }}
  >
    <div style={{
      width:44,height:44,borderRadius:10,flexShrink:0,
      background:color+'14',
      display:'flex',alignItems:'center',justifyContent:'center',
    }}>
      <i className={`ti ti-${icon}`} style={{fontSize:20,color}} aria-hidden="true"/>
    </div>
    <div>
      <div style={{fontSize:11.5,color:'#64748B',marginBottom:2,fontWeight:500}}>{label}</div>
      <div style={{fontSize:24,fontWeight:700,color:'#0F172A',lineHeight:1}}>{value}</div>
    </div>
  </div>
);

const HeroCard = ({ tag, title, sub, btnLabel, btnColor, textColor, onClick, icon, gradient }) => (
  <div style={{
    background:gradient,borderRadius:14,padding:'22px 24px',
    display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:14,
    boxShadow:'0 8px 28px rgba(0,0,0,.12)',
  }}>
    <div>
      <div style={{color:'rgba(255,255,255,.55)',fontSize:10,letterSpacing:'.09em',textTransform:'uppercase',marginBottom:6,fontWeight:600}}>{tag}</div>
      <div style={{color:'#fff',fontSize:16,fontWeight:700,marginBottom:4}}>{title}</div>
      <div style={{color:'rgba(255,255,255,.6)',fontSize:12}}>{sub}</div>
    </div>
    <button onClick={onClick} style={{
      background:'rgba(255,255,255,.95)',color:textColor,
      border:'none',borderRadius:8,padding:'9px 18px',
      fontSize:13,fontWeight:600,cursor:'pointer',
      display:'flex',alignItems:'center',gap:7,flexShrink:0,
      boxShadow:'0 4px 12px rgba(0,0,0,.15)',transition:'background .12s',
    }}
    onMouseEnter={e => e.currentTarget.style.background='#fff'}
    onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,.95)'}>
      <i className={`ti ti-${icon}`} style={{fontSize:15}} aria-hidden="true"/>
      {btnLabel}
    </button>
  </div>
);

export default function DashboardPage() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ currencies:0, insurance:0, users:0 });

  useEffect(() => {
    (async () => {
      try {
        const [c, ins] = await Promise.all([getCurrencies(), getInsurance()]);
        let u = { data:[] };
        if (isAdmin) { try { u = await getUsers(); } catch {} }
        setStats({ currencies:c.data.length, insurance:ins.data.length, users:u.data.length });
      } catch {}
    })();
  }, [isAdmin]);

  const hr = new Date().getHours();
  const greeting = hr < 12 ? 'Good morning' : hr < 17 ? 'Good afternoon' : 'Good evening';
  const today = new Date().toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long',year:'numeric'});

  return (
    <div>

      {/* Page header */}
      <div style={{marginBottom:24}}>
        <div style={{fontSize:12,color:'#94A3B8',marginBottom:5,letterSpacing:'.02em'}}>{today}</div>
        <h1 style={{fontSize:24,fontWeight:800,color:'#0F172A',letterSpacing:'-.4px',lineHeight:1.15}}>
          {greeting}, {user?.name?.split(' ')[0]}
        </h1>
        <p style={{color:'#64748B',marginTop:5,fontSize:13.5}}>
          Global contractor cost intelligence — 8 countries, instant results
        </p>
      </div>

      {/* Hero CTAs */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))',gap:14,marginBottom:24}}>
        <HeroCard
          tag="Single country"
          title="Run a calculation"
          sub="Saudi Arabia · India · Australia · US · Netherlands · Japan · Norway · China"
          btnLabel="Open calculator"
          btnColor="#0C1220"
          textColor="#0C1220"
          icon="calculator"
          gradient="linear-gradient(135deg,#0C1220 0%,#0EA5E9 130%)"
          onClick={() => navigate('/calculator')}
        />
        <HeroCard
          tag="Multi-country"
          title="Compare countries"
          sub="Side-by-side cost breakdown across markets"
          btnLabel="Compare now"
          btnColor="#065f46"
          textColor="#065f46"
          icon="arrows-diff"
          gradient="linear-gradient(135deg,#064E3B 0%,#10b981 130%)"
          onClick={() => navigate('/comparison')}
        />
      </div>

      {/* Stat widgets */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:12,marginBottom:24}}>
        <StatCard label="Countries supported" value="8"               icon="world"          color="#0EA5E9" onClick={() => navigate('/calculator')}/>
        <StatCard label="Insurance plans"      value={stats.insurance} icon="heart-plus"    color="#f59e0b" onClick={isAdmin ? () => navigate('/admin/insurance') : null}/>
        <StatCard label="Currencies"           value={stats.currencies} icon="currency-dollar" color="#8b5cf6" onClick={isAdmin ? () => navigate('/admin/currencies') : null}/>
        {isAdmin && <StatCard label="Users" value={stats.users} icon="users" color="#ef4444" onClick={() => navigate('/admin/users')}/>}
      </div>

      {/* Saved quotes shortcut */}
      <div style={{
        background:'#fff',border:'1px solid #E2E8F0',borderRadius:12,
        marginBottom:24,cursor:'pointer',transition:'box-shadow .15s,border-color .15s',
      }}
        onClick={() => navigate('/calculations')}
        onMouseEnter={e => { e.currentTarget.style.boxShadow='0 6px 20px rgba(0,0,0,.08)'; e.currentTarget.style.borderColor='#CBD5E1'; }}
        onMouseLeave={e => { e.currentTarget.style.boxShadow=''; e.currentTarget.style.borderColor='#E2E8F0'; }}>
        <div style={{padding:'16px 20px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:16,flexWrap:'wrap'}}>
          <div style={{display:'flex',alignItems:'center',gap:14}}>
            <div style={{width:42,height:42,borderRadius:10,background:'#EFF6FF',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
              <i className="ti ti-history" style={{fontSize:20,color:'#2563EB'}} aria-hidden="true"/>
            </div>
            <div>
              <div style={{fontWeight:700,color:'#0F172A',fontSize:14.5}}>Saved quotes</div>
              <div style={{fontSize:12.5,color:'#64748B',marginTop:2}}>Review and revisit previous calculations</div>
            </div>
          </div>
          <span style={{fontSize:13,color:'#0EA5E9',fontWeight:600,display:'flex',alignItems:'center',gap:4}}>
            View all <i className="ti ti-arrow-right" style={{fontSize:14}} aria-hidden="true"/>
          </span>
        </div>
      </div>

      {/* About — admins only */}
      {isAdmin && (
        <div style={{background:'#fff',border:'1px solid #E2E8F0',borderRadius:12,overflow:'hidden'}}>
          <div style={{
            padding:'12px 18px',borderBottom:'1px solid #E2E8F0',
            background:'linear-gradient(90deg,rgba(14,165,233,.04) 0%,transparent 100%)',
            display:'flex',alignItems:'center',gap:9,
          }}>
            <div style={{width:7,height:7,borderRadius:'50%',background:'#0EA5E9',flexShrink:0}}/>
            <span style={{fontWeight:600,color:'#0F172A',fontSize:13.5}}>About Quoteology</span>
          </div>
          <div style={{padding:'16px 18px'}}>
            <p style={{fontSize:13.5,color:'#475569',lineHeight:1.75}}>
              <strong style={{color:'#0F172A'}}>Quoteology</strong> replaces Excel cost modelling with a fast, accurate platform that follows actual tax law — progressive brackets, statutory deductions, and city-level social insurance — not rough estimates.
            </p>
            <p style={{fontSize:13.5,color:'#475569',lineHeight:1.75,marginTop:10}}>
              Supports <strong style={{color:'#0F172A'}}>Saudi Arabia</strong>, <strong style={{color:'#0F172A'}}>India</strong>, <strong style={{color:'#0F172A'}}>Australia</strong>, <strong style={{color:'#0F172A'}}>United States</strong>, <strong style={{color:'#0F172A'}}>Netherlands</strong>, <strong style={{color:'#0F172A'}}>Japan</strong>, <strong style={{color:'#0F172A'}}>Norway</strong>, and <strong style={{color:'#0F172A'}}>China</strong>. Export quotes as branded PDF or Excel in one click.
            </p>
            <p style={{fontSize:13.5,color:'#475569',lineHeight:1.75,marginTop:10}}>
              All tax rates, social insurance, and burdens are fully configurable by administrators without touching code.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}